/* Hanwu SGS 2.2: UI state only. Identity is server-rendered by Shopify.
 * No password, email, customer ID or authentication token is stored by this file.
 * Reading preferences persist locally; profile editing is confirmed server-side and synced to Shopify Customers.
 */
(() => {
  'use strict';
  const KEY = 'hanwu.reading.v2';
  const validPanels = new Set(['overview','profile','security','guild','forum','events','preferences','loyalty','admin','avatar']);
  const defaults = () => ({compact:false,reduceMotion:false});
  const clean = value => ({compact:value?.compact === true,reduceMotion:value?.reduceMotion === true});
  let prefs = defaults();
  let canStore = true;
  try { prefs = clean(JSON.parse(localStorage.getItem(KEY) || '{}')); } catch (_) { canStore = false; }
  const activeHubs = new Set();
  function applyPrefs() {
    activeHubs.forEach(hub => {
      if (!hub.isConnected) { activeHubs.delete(hub); return; }
      hub.dataset.density = prefs.compact ? 'compact' : 'comfortable';
      hub.dataset.reduceMotion = String(prefs.reduceMotion);
      hub.querySelectorAll('[data-pref]').forEach(input => { input.checked = prefs[input.dataset.pref] === true; });
    });
  }
  function getPanelFromHash() {
    const hash = window.location.hash.slice(1);
    if (hash === 'membership') return 'overview';
    if (!hash.startsWith('membership-')) return null;
    const key = hash.slice('membership-'.length);
    return validPanels.has(key) ? key : null;
  }
  function init(hub) {
    if (hub.dataset.enhanced) return;
    hub.dataset.enhanced = 'true'; activeHubs.add(hub);
    const panes = [...hub.querySelectorAll('[data-hw22-panel]')];
    const tabs = [...hub.querySelectorAll('[data-hw22-tab]')];
    function show(key, focus = false) {
      if (!validPanels.has(key) || (key === 'admin' && hub.dataset.hw29Admin !== 'true')) key = 'overview';
      hub.dataset.activePanel = key;
      panes.forEach(pane => { pane.hidden = pane.dataset.hw22Panel !== key; });
      tabs.forEach(tab => { if (tab.dataset.hw22Tab === key) tab.setAttribute('aria-current','page'); else tab.removeAttribute('aria-current'); });
      if (focus) panes.find(pane => pane.dataset.hw22Panel === key)?.focus({preventScroll:true});
    }
    // Kept in closure for the role check and old bookmarked admin/loyalty paths.
    let requestedPanel=getPanelFromHash() || hub.dataset.initialPanel || 'overview';
    if (hub.dataset.currentHandle === 'admin' || hub.dataset.currentHandle === 'loyalty') {
      requestedPanel=hub.dataset.currentHandle;
      try {
        const target=new URL(hub.dataset.memberHubUrl,window.location.origin);
        if(target.origin===window.location.origin && target.pathname!==window.location.pathname){
          target.hash='membership-'+requestedPanel;
          window.location.replace(target.href);
        }
      } catch (_) { /* The server-rendered membership page is a safe fallback. */ }
    }
    show(requestedPanel);
    const applyCommunitySession = event => {
      const state=event.detail;
      const admin=!!(state && state.authenticated && state.user && state.user.is_admin);
      hub.dataset.hw29Admin=String(admin);
      hub.querySelectorAll('[data-hw29-admin-entry]').forEach(node => {node.hidden=!admin;});
      if(!admin && hub.dataset.activePanel==='admin')show('overview');
      if(state && state.authenticated){
        hub.querySelectorAll('[data-hw29-role]').forEach(n=>{n.textContent=state.user.role_label;});
        const progress=hub._hw33Progress?.id===state.user.id?hub._hw33Progress.progress:state.progress;
        hub.querySelectorAll('[data-hw29-points]').forEach(n=>{n.textContent=String(progress?.xp??'—')+' 分';});
        hub.querySelectorAll('[data-hw29-level]').forEach(n=>{n.textContent=(progress?.level?.name||'')+(state.program?.enabled?' · 成长计划已启用':' · 成长计划待启用');});
        const labels={pending:'待审批',approved:'认证待核对公会',rejected:'申请未通过',revoked:'认证已撤销'};
        hub.querySelectorAll('[data-hw29-cert-status]').forEach(n=>{n.textContent=state.certification?.is_verified?'已认证':labels[state.certification?.status]||'尚未提交社区认证';});
      }
      hub.querySelectorAll('[data-hw3-cert-info]').forEach(n=>{n.textContent=state?.authenticated&&state.certification?.is_verified?state.certification.display_info:'';});
      hub.querySelectorAll('[data-hw3-position]').forEach(n=>{n.textContent=state?.authenticated?(state.user.guild_position||'公会职务待填写'):'公会职务待核验';});
      if(state?.authenticated)updateAvatar(hub,state.avatar,hub.querySelector('[data-proxy]')?.dataset.proxy||'/apps/hanwu-community');else updateAvatar(hub,null,'/apps/hanwu-community');
      if(admin && (getPanelFromHash()==='admin' || requestedPanel==='admin')){show('admin');requestedPanel='overview';}
    };
    hub.addEventListener('hanwu:community-session',applyCommunitySession);
    hub.addEventListener('hanwu:progress-updated',event=>{const p=event.detail;if(!p?.progress)return;hub.querySelectorAll('[data-hw29-points]').forEach(n=>n.textContent=p.progress.xp+' 分');hub.querySelectorAll('[data-hw29-level]').forEach(n=>n.textContent=(p.progress.level?.name||'')+' · 成长计划已启用');});
    hub.addEventListener('hanwu:profile-synced',event=>{
      const p=event.detail;if(!p)return;
      hub.querySelectorAll('[data-hw210-name]').forEach(n=>n.textContent=p.nickname||p.first_name||'汉武同袍');
      hub.querySelectorAll('[data-hw3-position]').forEach(n=>n.textContent=p.guild_position||'公会职务待填写');
      hub.querySelectorAll('[data-hw210-bio]').forEach(n=>n.textContent=p.bio||'同道同行，天下可期。');
      const complete=['first_name','email','nickname','region','bio'].filter(k=>!!p[k]).length*20;
      hub.querySelectorAll('.hw22-ring').forEach(n=>{n.style.setProperty('--progress',complete+'%');const b=n.querySelector('b');if(b)b.textContent=complete+'%';});
    });
    // The API bridge also stores a NON-CREDENTIAL snapshot to handle defer execution order.
    if(hub._hanwuDisplayState)applyCommunitySession({detail:hub._hanwuDisplayState});

    hub.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target.closest('[data-hw22-tab],[data-hw22-go]') : null;
      if (!target || !hub.contains(target) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
      const key = target.dataset.hw22Tab || target.dataset.hw22Go;
      if (!validPanels.has(key) || (key==='admin' && hub.dataset.hw29Admin!=='true')) return;
      event.preventDefault();
      const url = new URL(window.location.href); url.hash = `membership-${key}`;
      try { history.pushState(null,'',url.href); } catch (_) { window.location.hash = url.hash; }
      show(key, true);
      if (hub.getBoundingClientRect().top < -220) hub.scrollIntoView({block:'start',behavior:'auto'});
    });
    const sync = () => { const key = getPanelFromHash(); if (key && hub.isConnected) show(key); };
    window.addEventListener('hashchange',sync); window.addEventListener('popstate',sync);
    hub.addEventListener('hanwu:dispose',()=>{window.removeEventListener('hashchange',sync);window.removeEventListener('popstate',sync);activeHubs.delete(hub);});
    hub.querySelectorAll('[data-hw22-preferences]').forEach(form => {
      const status = form.querySelector('[data-prefs-status]');
      form.querySelector('[data-prefs-save]').disabled = false;
      form.querySelector('[data-prefs-reset]').disabled = false;
      if (!canStore) status.textContent = '当前浏览器限制了本地存储；仍可在本页预览偏好。';
      form.addEventListener('submit',event=>{
        event.preventDefault();
        const next = clean({compact:form.querySelector('[data-pref="compact"]').checked,reduceMotion:form.querySelector('[data-pref="reduceMotion"]').checked});
        prefs = next; applyPrefs();
        try {localStorage.setItem(KEY,JSON.stringify(next));canStore=true;status.textContent='已保存到当前浏览器；未更改 Shopify 账号资料。';}
        catch (_) {canStore=false;status.textContent='已应用于本页，但浏览器阻止了保存；刷新后可能恢复默认。';}
      });
      form.querySelector('[data-prefs-reset]').addEventListener('click',()=>{
        prefs=defaults(); applyPrefs();
        try {localStorage.removeItem(KEY);status.textContent='已恢复本机默认阅读偏好。';}
        catch (_) {status.textContent='本页已恢复默认，但无法清除受限制的浏览器存储。';}
      });
    });
    applyPrefs();
    hub.querySelectorAll('[data-hw22-feed]').forEach(loadFeed);
  }
  const node = (tag,text,cls) => {const el=document.createElement(tag);if(text!==null)el.textContent=text;if(cls)el.className=cls;return el;};
  async function loadFeed(root) {
    const status=root.querySelector('[data-feed-status]');const list=root.querySelector('[data-feed-items]');
    let base;
    try {base=new URL(root.dataset.forumBase);if(base.protocol!=='https:' || base.pathname!=='/' || base.search || base.hash || base.username || base.password)throw new Error('Invalid root');}
    catch (_) {status.textContent='论坛地址不符合 HTTPS 根地址格式。';root.dataset.connection='error';return;}
    const controller=new AbortController();const timer=setTimeout(()=>controller.abort(),8000);
    try {
      const response=await fetch(`${base.origin}/api/public/summary`,{credentials:'omit',headers:{Accept:'application/json'},signal:controller.signal});
      if(!response.ok)throw new Error('HTTP response');
      const data=await response.json();
      if(data.service!=='hanwu-community' || !Array.isArray(data.topics))throw new Error('Unexpected service');
      const rows=[];
      data.topics.slice(0,4).forEach(topic=>{
        if(!topic || typeof topic.url!=='string')return;
        let url;try{url=new URL(topic.url);if(url.origin!==base.origin || !/^\/topic\/[a-f0-9]+\/$/.test(url.pathname) || url.search || url.hash || url.username || url.password)return;}catch(_){return;}
        const row=node('article',null,'hw22-feed-item');const link=node('a',String(topic.title||'未命名主题').slice(0,200));link.href=url.href;
        row.append(node('span',String(topic.board||'公开主题').slice(0,80),'hw22-feed-board'),link,node('small',`${String(topic.date||'').slice(0,30)} · ${Number.isSafeInteger(topic.replies)&&topic.replies>=0?topic.replies:0} 回复`));rows.push(row);
      });
      list.replaceChildren(...rows);
      if(!rows.length)list.append(node('p','尚无可展示的审核通过公开主题。','hw22-note'));
      status.textContent='已读取公开帖子 · 不含私密内容';root.dataset.connection='ok';
    } catch (_) {
      status.textContent='公开动态暂不可用，请直接进入论坛查看。';root.dataset.connection='error';
      const link=node('a','打开公会论坛 →','text-link');link.href=`${base.origin}/`;list.replaceChildren(link);
    } finally {clearTimeout(timer);}
  }
  function updateAvatar(scope,avatar,prefix){
    const id=avatar?.active?.id;
    const ok=/^[a-f0-9]{32}$/.test(id||'')&&/^\/apps\/[a-z0-9_-]+$/.test(prefix||'');
    scope.querySelectorAll('[data-hw3-avatar-image]').forEach(img=>{
      img.hidden=true;const box=img.parentElement;box.querySelectorAll('[data-hw3-avatar-text]').forEach(n=>n.hidden=false);
      if(ok){img.onload=()=>{img.hidden=false;box.querySelectorAll('[data-hw3-avatar-text]').forEach(n=>n.hidden=true);};img.onerror=()=>{img.hidden=true;box.querySelectorAll('[data-hw3-avatar-text]').forEach(n=>n.hidden=false);};img.src=prefix+'/v1/avatars/'+id+'/image';}
      else img.removeAttribute('src');
    });
  }
  document.addEventListener('hanwu:avatar-session',event=>{
    const {state,prefix}=event.detail||{};
    document.querySelectorAll('[data-hw3-avatar-menu]').forEach(menu=>updateAvatar(menu,state?.authenticated?state.avatar:null,prefix));
  });
  async function loadHeaderAvatar(menu){
    const prefix=menu.dataset.proxy||'/apps/hanwu-community';if(!/^\/apps\/[a-z0-9_-]+$/.test(prefix))return;
    const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),8000);
    try{const r=await fetch(prefix+'/v1/session?_hw_json_errors=1',{credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'},signal:controller.signal});if(!r.ok||!(r.headers.get('content-type')||'').includes('application/json'))return;const j=await r.json();if(j.authenticated&&j.ok!==false&&j.role_model==='hanwu-membership-v4')updateAvatar(menu,j.avatar,prefix);}catch(_){/* No pretend avatar or auth on failure. */}finally{clearTimeout(timer);}
  }
  document.querySelectorAll('[data-hw3-avatar-menu]').forEach(loadHeaderAvatar);
  function scan(scope=document){scope.querySelectorAll('[data-hw22-hub]').forEach(init);}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>scan(),{once:true});else scan();
  document.addEventListener('shopify:section:load',event=>scan(event.target));
  document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('[data-hw22-hub]').forEach(hub=>hub.dispatchEvent(new Event('hanwu:dispose'))));
  window.addEventListener('storage',event=>{if(event.key!==KEY)return;try{prefs=clean(JSON.parse(event.newValue||'{}'));applyPrefs();}catch(_){/* Ignore malformed non-auth preference data. */}});
})();
